import { isEmpty } from "lodash";
import { BaseIdOnly } from "../base";
import { RunnerConfig, RunnerConfigParam } from "../job";
import {
  DEFAULT_LANGUAGE,
  Language,
  Translation,
  translate,
} from "../language";
import { Moonpass } from "../moonpass";
import { AirdropConfig, AirdropConfigParam, NFTOwnership } from "../nft";
import { PaymentConfig, PaymentConfigParam } from "../payment";
import { toJSON } from "../utils";
import {
  NFTEventTierMoonpassConstraint,
  NFTEventTierMoonpassConstraintParam,
} from "./constraint/moonpass";
import { NFTConstraint, NFTConstraintParam } from "./constraint/nft";
import { NFTEventTierEmailConfig, NFTEventTierEmailConfigParam } from "./email";
import {
  NFTEventTierRewardConfig,
  NFTEventTierRewardConfigParam,
} from "./reward";
import {
  NFTEventTierUsageConstraint,
  NFTEventTierUsageConstraintParam,
} from "./usage";
import {
  NFTEventTierWhitelistConstraint,
  NFTEventTierWhitelistConstraintParam,
} from "./whitelist";

export interface NFTEventTierInfo {
  name?: string;
  description?: string;
  rewards?: string[];
  instruction?: string; // @deprecated
}

export interface NFTEventTierParam
  extends Omit<
    Partial<NFTEventTier>,
    | "whitelistConstraint"
    | "nftConstraint"
    | "moonpassConstraint"
    | "usageConstraint"
    | "paymentConfig"
    | "airdropConfig"
    | "emailConfig"
    | "attendanceAirdropConfig"
    | "runnerConfig"
    | "rewardConfig"
  > {
  whitelistConstraint?: NFTEventTierWhitelistConstraintParam;
  nftConstraint?: NFTConstraintParam;
  moonpassConstraint?: NFTEventTierMoonpassConstraintParam;
  usageConstraint?: NFTEventTierUsageConstraintParam;
  paymentConfig?: PaymentConfigParam;
  airdropConfig?: AirdropConfigParam;
  emailConfig?: NFTEventTierEmailConfigParam;
  attendanceAirdropConfig?: AirdropConfigParam;
  runnerConfig?: RunnerConfigParam;
  rewardConfig?: NFTEventTierRewardConfigParam;

  // @deprecated
  name?: string;
  description?: string;
  rewards?: string[];
  instruction?: string;
}
export class NFTEventTier extends BaseIdOnly {
  order: number;
  info: Translation<NFTEventTierInfo>;
  imageUrl: string;
  eligible?: boolean;
  whitelistConstraint?: NFTEventTierWhitelistConstraint;
  nftConstraint?: NFTConstraint;
  moonpassConstraint?: NFTEventTierMoonpassConstraint; // Only set if user owns specific moonpass, allow access without further checking
  usageConstraint?: NFTEventTierUsageConstraint;
  paymentConfig?: PaymentConfig;
  airdropConfig?: AirdropConfig;
  emailConfig?: NFTEventTierEmailConfig;
  attendanceAirdropConfig?: AirdropConfig;
  runnerConfig?: RunnerConfig;
  rewardConfig?: NFTEventTierRewardConfig;

  // NFT metadata, non-changeable after deploy
  nftName: string;
  nftDescription: string;

  constructor(params?: NFTEventTierParam) {
    super(params);
    this.order = params?.order ?? 1;
    this.info = {};
    if (params?.info && !isEmpty(params?.info)) {
      this.info = toJSON(params.info);
      for (const [language, info] of Object.entries(params.info)) {
        this.info[language as Language]!.description =
          info.instruction || info.description || "";
        delete this.info[language as Language]!.instruction;
      }
    } else {
      this.info[DEFAULT_LANGUAGE] = {
        name: params?.name || "",
        description: params?.instruction || params?.description || "",
        rewards: params?.rewards || [],
      };
    }

    this.nftName = params?.nftName || this.name || "";
    this.nftDescription =
      params?.nftDescription ||
      this.name ||
      translate(params?.info).description ||
      this.description ||
      "";

    this.imageUrl = params?.imageUrl || "";
    if (params?.nftConstraint && !isEmpty(params.nftConstraint.collections)) {
      this.nftConstraint = new NFTConstraint(params.nftConstraint);
      if (this.nftConstraint.collections.length == 0) delete this.nftConstraint;
    }
    if (params?.whitelistConstraint)
      this.whitelistConstraint = new NFTEventTierWhitelistConstraint(
        this.nftConstraint
          ? {
              ...params.whitelistConstraint,
              nftRequirement: Math.max(
                1,
                params.whitelistConstraint.nftRequirement || 0
              ),
            }
          : params.whitelistConstraint
      );
    if (params?.moonpassConstraint)
      this.moonpassConstraint = new NFTEventTierMoonpassConstraint(
        params.moonpassConstraint
      );
    this.usageConstraint = new NFTEventTierUsageConstraint(
      params?.usageConstraint
    );
    if (params?.paymentConfig) {
      if (params.paymentConfig.autoDiscounts) {
        params.paymentConfig.autoDiscounts.forEach((config) => {
          config.discount.productId = this.id;
        });
      }
      this.paymentConfig = new PaymentConfig(params.paymentConfig);
    }
    if (params?.airdropConfig)
      this.airdropConfig = new AirdropConfig(params.airdropConfig);
    if (params?.attendanceAirdropConfig)
      this.attendanceAirdropConfig = new AirdropConfig(
        params.attendanceAirdropConfig
      );
    if (params?.emailConfig)
      this.emailConfig = new NFTEventTierEmailConfig(params.emailConfig);
    if (params?.runnerConfig)
      this.runnerConfig = new RunnerConfig(params.runnerConfig);
    if (params?.rewardConfig)
      this.rewardConfig = new NFTEventTierRewardConfig(params.rewardConfig);
  }

  get name() {
    return translate(this.info).name || "";
  }

  get description() {
    return translate(this.info).description || "";
  }

  get rewards() {
    return translate(this.info).rewards || [];
  }

  getEligibleNFTCollectionCount(nfts: NFTOwnership[]) {
    const eligibleCollections = this.nftConstraint?.validate(nfts) || [];
    if (eligibleCollections.length == 0) return 0;
    return Math.min(...eligibleCollections.map((e) => e.tokens.length));
  }

  validateNFTConstraint(nfts: NFTOwnership[]) {
    return (this.nftConstraint?.validate(nfts) || []).length > 0;
  }

  validateMoonpassConstraint(moonpass?: Moonpass) {
    if (!this.moonpassConstraint) return true;
    if (!moonpass) return false;
    if (this.moonpassConstraint.edition == "all") return true;
    return this.moonpassConstraint.edition === moonpass.edition;
  }
}
